import axios from 'axios';
import * as Sentry from '@sentry/browser';
import Lock from 'browser-tabs-lock';

export const TOKEN_TYPES = {
    HERE: 'HereToken',
    JETLAG: 'JetLagToken',
    LIVETRACK: 'LiveTrackToken'
};

class HereTokenManager {
    constructor() {
        this.hereTokenManager = new TokenManager(TOKEN_TYPES.HERE);
        this.hereJetLagTokenManager = new TokenManager(TOKEN_TYPES.JETLAG);
    }

    async initialize() {
        await this.hereTokenManager.initialize();
        await this.hereJetLagTokenManager.initialize();
    }

    async getJetLagToken() {
        return await this.hereJetLagTokenManager.getToken();
    }

    async getHereToken() {
        return await this.hereTokenManager.getToken();
    }

    async getToken() {
        return await this.getHereToken();
    }
}

class TokenManager {
    timeoutDuration = 10000;
    expiryAdjustment = 60 * 1000;

    constructor(tokenType) {
        this.axiosInstance = axios.create({
            baseURL: '/'
        });

        this.lock = new Lock();

        this.tokenType = tokenType;
    }

    async initialize() {
        if (!this.accessToken) {
            try {
                await this.getToken();
            } catch (error) {
                Sentry.captureException(error);
            }
        }
    }

    async fetchHereToken() {
        try {
            const data = {
                tokenType: this.tokenType
            };
            const resp = await this.axiosInstance.post('/services/auth/token/here', data);

            const token = resp.data;
            token.expires = Date.now() + token.expires_in * 1000 - this.expiryAdjustment;

            this.token = token;
            return token.access_token;
        } catch (error) {
            if (error.config) {
                throw this.addErrorContext(error);
            }
            throw error;
        }
    }

    async fetchToken() {
        if (await this.lock.acquireLock(`fetch${this.tokenType}`, this.timeoutDuration)) {
            // lock has been acquired
            try {
                // Check for the token again because it might have been fetched while waiting for the lock
                if (this.accessToken) {
                    return this.accessToken;
                }

                return await this.fetchHereToken();
            } catch (e) {
                throw e;
            } finally {
                await this.lock.releaseLock(`fetch${this.tokenType}`);
            }
        } else {
            throw new Error('Timeout acquiring lock');
        }
    }

    removeToken() {
        localStorage.removeItem(this.tokenType);
    }

    addErrorContext(error) {
        // Create new Error that contains the URL from the API call
        let wrappedError = new Error(`${error.message} - ${error.config.url}`);

        // Add custom Axios fields back to the new Error
        wrappedError.config = error.config;
        wrappedError.request = error.request;
        wrappedError.response = error.response;

        return wrappedError;
    }

    async getToken() {
        // Return the token from storage if there is a valid one
        if (this.accessToken) {
            return this.accessToken;
        }

        // singlePromise is used to make sure that we only have 1 call fetching a token at a time regardless of the number of concurrent API requests
        // It internally keeps track of a promise and will return the same promise to additional calls until the promise resolves
        const singlePromise = (callback) => {
            if (!this.getTokenPromise) {
                this.getTokenPromise = callback().finally(() => {
                    this.getTokenPromise = null;
                });
            }
            return this.getTokenPromise;
        };

        return singlePromise(async () => {
            try {
                return await this.fetchToken();
            } catch (error) {
                Sentry.captureException(error);
            }
        });
    }

    get token() {
        const tokenString = localStorage.getItem(this.tokenType);

        if (tokenString) {
            const token = JSON.parse(tokenString);

            // Remove the token if it is expired
            const now = Date.now();
            if (token.expires < now && (!token.refresh_token || token.refresh_token_expires < now)) {
                this.removeToken();
                return;
            }

            return token;
        }
    }

    set token(token) {
        if (token) {
            localStorage.setItem(this.tokenType, JSON.stringify(token));
        }
    }

    get accessToken() {
        if (this.token && this.token.expires > Date.now()) {
            return this.token.access_token;
        }
    }
}

export default new HereTokenManager();
